import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import StatusBadge from '../components/StatusBadge';
import PageTitle from '../components/PageTitle';

const API_BASE = process.env.REACT_APP_API_URL || '';

// ── ITR Progress steps ────────────────────────────────────────────
const ITR_STEPS = [
  { key: 'SUBMITTED',       label: 'Submitted',       desc: 'Your filing has been received by our CA team.' },
  { key: 'UNDER_REVIEW',    label: 'Under Review',    desc: 'Our CA is reviewing your documents and preparing your return.' },
  { key: 'ACTION_REQUIRED', label: 'Action Required', desc: 'Your CA needs additional information or corrections from you.' },
  { key: 'DRAFT_SENT',      label: 'Draft Sent',      desc: 'Draft ITR sent to your email. Please review and approve below.' },
  { key: 'APPROVED',        label: 'Approved',        desc: 'You approved the draft. Our CA is filing with the Income Tax Dept.' },
  { key: 'FILED',           label: 'Filed',           desc: 'ITR has been e-filed with the Income Tax Department.' },
  { key: 'ACKNOWLEDGED',    label: 'Acknowledged',    desc: 'ITR-V received. Download your acknowledgement below.' },
];

// ── GST Progress steps ────────────────────────────────────────────
const GST_STEPS = [
  { key: 'SUBMITTED',             label: 'Submitted',      desc: 'Application received by our CA team.' },
  { key: 'DOCUMENTS_VERIFIED',    label: 'Docs Verified',  desc: 'Your documents have been verified.' },
  { key: 'APPLICATION_FILED',     label: 'Filed',          desc: 'Application filed on GST portal. ARN generated.' },
  { key: 'GSTIN_ASSIGNED',        label: 'GSTIN Assigned', desc: 'Your GSTIN has been assigned. Download certificate below.' },
];

const GST_STATUS_LABEL = {
  SUBMITTED:             { label: 'Submitted',            icon: '📨' },
  DOCUMENTS_VERIFIED:    { label: 'Docs Verified',        icon: '✅' },
  APPLICATION_FILED:     { label: 'Filed on Portal',      icon: '🏛' },
  PENDING_CLARIFICATION: { label: 'Pending Clarification',icon: '⚠️' },
  GSTIN_ASSIGNED:        { label: 'GSTIN Assigned',       icon: '🎉' },
  REJECTED:              { label: 'Rejected',             icon: '❌' },
};

function GstBadge({ status }) {
  const s = GST_STATUS_LABEL[status] || { label: status, icon: '•' };
  const cls = { SUBMITTED:'badge-submitted', DOCUMENTS_VERIFIED:'badge-approved', APPLICATION_FILED:'badge-filed', GSTIN_ASSIGNED:'badge-acknowledged', REJECTED:'badge-rejected', PENDING_CLARIFICATION:'badge-under_review' };
  return <span className={`badge ${cls[status] || ''}`}>{s.icon} {s.label}</span>;
}

// ── Applications List (ITR + GST combined) ─────────────────────────
export function ApplicationsList() {
  const [itrFilings, setItrFilings] = useState([]);
  const [gstApps,    setGstApps]    = useState([]);
  const [tab,        setTab]        = useState('all');
  const [loading,    setLoading]    = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/api/filing/my'),
      api.get('/api/gst/my'),
    ]).then(([itr, gst]) => {
      setItrFilings(itr.data);
      setGstApps(gst.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="centered"><span className="spinner spinner-dark" /></div>;

  const totalPending = itrFilings.filter(f => f.status === 'DRAFT_SENT').length +
                       gstApps.filter(g => g.status === 'PENDING_CLARIFICATION').length;

  return (
    <>
      <PageTitle title="My Applications" />
      <div className="page-hero">
        <div className="page-hero-label">My Applications</div>
        <h1>Filing & Registration History</h1>
        <p>Track all your ITR filings and GST registrations in one place</p>
      </div>
      <div className="page">
        {/* Action buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', gap: '.5rem' }}>
            {[['all', `All (${itrFilings.length + gstApps.length})`], ['itr', `ITR Filings (${itrFilings.length})`], ['gst', `GST (${gstApps.length})`]].map(([key, label]) => (
              <button key={key} onClick={() => setTab(key)}
                style={{ padding: '.45rem 1rem', borderRadius: 8, border: `1.5px solid ${tab === key ? 'var(--navy)' : 'var(--border)'}`, background: tab === key ? 'var(--navy)' : '#fff', color: tab === key ? '#fff' : 'var(--muted)', fontFamily: 'DM Sans, sans-serif', fontSize: '.82rem', fontWeight: 500, cursor: 'pointer' }}>
                {label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '.75rem' }}>
            <Link to="/file"><button className="btn btn-outline btn-sm">+ File ITR</button></Link>
            <Link to="/gst"><button className="btn btn-gold btn-sm">+ GST Registration</button></Link>
          </div>
        </div>

        {totalPending > 0 && (
          <div className="alert alert-info" style={{ marginBottom: '1.5rem' }}>
            ⚠️ You have <strong>{totalPending}</strong> application{totalPending > 1 ? 's' : ''} requiring your attention.
          </div>
        )}

        {/* ITR filings */}
        {(tab === 'all' || tab === 'itr') && (
          <>
            {tab === 'all' && itrFilings.length > 0 && (
              <div style={{ fontWeight: 600, fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--gold)', marginBottom: '.75rem' }}>
                Income Tax Returns
              </div>
            )}
            {itrFilings.length === 0 && tab === 'itr' ? (
              <EmptyState title="No ITR filings yet" desc="File your income tax return and track its progress here." link="/file" linkLabel="File ITR →" />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: tab === 'all' && gstApps.length > 0 ? '2rem' : 0 }}>
                {itrFilings.map(f => <ItrCard key={f.id} filing={f} />)}
              </div>
            )}
          </>
        )}

        {/* GST applications */}
        {(tab === 'all' || tab === 'gst') && (
          <>
            {tab === 'all' && gstApps.length > 0 && (
              <div style={{ fontWeight: 600, fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--gold)', marginBottom: '.75rem' }}>
                GST Registrations
              </div>
            )}
            {gstApps.length === 0 && tab === 'gst' ? (
              <EmptyState title="No GST applications yet" desc="Register your business for GST and track the status here." link="/gst" linkLabel="Apply for GST →" />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {gstApps.map(g => <GstCard key={g.id} app={g} />)}
              </div>
            )}
          </>
        )}

        {tab === 'all' && itrFilings.length === 0 && gstApps.length === 0 && (
          <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
            <h3 style={{ color: 'var(--navy)', marginBottom: '.5rem' }}>No applications yet</h3>
            <p style={{ color: 'var(--muted)', marginBottom: '1.5rem' }}>Start by filing your ITR or applying for GST registration</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <Link to="/file"><button className="btn btn-primary">File ITR →</button></Link>
              <Link to="/gst"><button className="btn btn-gold">GST Registration →</button></Link>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function EmptyState({ title, desc, link, linkLabel }) {
  return (
    <div className="card" style={{ textAlign: 'center', padding: '2.5rem' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '.75rem' }}>📋</div>
      <h3 style={{ color: 'var(--navy)', marginBottom: '.4rem', fontSize: '1rem' }}>{title}</h3>
      <p style={{ color: 'var(--muted)', marginBottom: '1.25rem', fontSize: '.88rem' }}>{desc}</p>
      <Link to={link}><button className="btn btn-primary btn-sm">{linkLabel}</button></Link>
    </div>
  );
}

function ItrCard({ filing: f }) {
  const idx = ITR_STEPS.findIndex(s => s.key === f.status);
  const needsAction = f.status === 'DRAFT_SENT' || f.status === 'ACTION_REQUIRED';
  return (
    <div className="card" style={needsAction ? { border: '2px solid var(--gold)' } : {}}>
      {f.status === 'DRAFT_SENT' && (
        <div style={{ background: 'var(--gold)', color: 'var(--navy)', padding: '.35rem 1rem', margin: '-2rem -2rem 1.25rem', borderRadius: '14px 14px 0 0', fontSize: '.78rem', fontWeight: 600 }}>
          ⚠️ Action Required — Review and approve your draft ITR
        </div>
      )}
      {f.status === 'ACTION_REQUIRED' && (
        <div style={{ background: '#E65100', color: '#fff', padding: '.35rem 1rem', margin: '-2rem -2rem 1.25rem', borderRadius: '14px 14px 0 0', fontSize: '.78rem', fontWeight: 600 }}>
          ⚠️ Action Required — Your CA needs additional information or corrections
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.1rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.25rem' }}>
            <span style={{ background: 'var(--bluel)', color: 'var(--blue)', fontSize: '.68rem', fontWeight: 700, padding: '.15rem .5rem', borderRadius: 4 }}>ITR</span>
            <span style={{ fontFamily: 'Playfair Display, serif', fontSize: '1rem', color: 'var(--navy)' }}>{f.itrType} — AY {f.assessmentYr}</span>
          </div>
          <div style={{ fontSize: '.78rem', color: 'var(--muted)' }}>Filing #{f.id} · {f.pan} · ₹{Number(f.annualIncome || 0).toLocaleString('en-IN')}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
          <StatusBadge status={f.status} />
          <Link to={`/applications/itr/${f.id}`}><button className="btn btn-outline btn-sm">Details →</button></Link>
        </div>
      </div>
      {f.status === 'REJECTED' ? (
        <div className="alert alert-error">❌ {f.adminNote || 'Filing rejected. Contact our CA team.'}</div>
      ) : (
        <ProgressTracker steps={ITR_STEPS} currentStatus={f.status} />
      )}
      {f.adminNote && f.status !== 'REJECTED' && (
        <div style={{ marginTop: '.75rem', background: 'var(--bluel)', border: '1px solid rgba(26,95,180,.15)', borderRadius: 8, padding: '.7rem 1rem', fontSize: '.82rem', color: 'var(--blue)' }}>
          💬 CA: {f.adminNote}
        </div>
      )}
      <div style={{ fontSize: '.72rem', color: 'var(--muted)', marginTop: '.6rem' }}>
        Submitted {new Date(f.submittedAt).toLocaleDateString('en-IN')}
      </div>
    </div>
  );
}

function GstCard({ app: g }) {
  const idx = GST_STEPS.findIndex(s => s.key === g.status);
  const needsAction = g.status === 'PENDING_CLARIFICATION';
  return (
    <div className="card" style={needsAction ? { border: '2px solid var(--gold)' } : {}}>
      {needsAction && (
        <div style={{ background: 'var(--gold)', color: 'var(--navy)', padding: '.35rem 1rem', margin: '-2rem -2rem 1.25rem', borderRadius: '14px 14px 0 0', fontSize: '.78rem', fontWeight: 600 }}>
          ⚠️ Clarification Required — Our CA needs additional information
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.1rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.25rem' }}>
            <span style={{ background: 'var(--greenl)', color: 'var(--green)', fontSize: '.68rem', fontWeight: 700, padding: '.15rem .5rem', borderRadius: 4 }}>GST</span>
            <span style={{ fontFamily: 'Playfair Display, serif', fontSize: '1rem', color: 'var(--navy)' }}>{g.businessName}</span>
          </div>
          <div style={{ fontSize: '.78rem', color: 'var(--muted)' }}>
            App #{g.id} · {g.businessType} · {g.pan}
            {g.gstin && <span style={{ marginLeft: '.5rem', color: 'var(--green)', fontWeight: 600 }}>GSTIN: {g.gstin}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
          <GstBadge status={g.status} />
          <Link to={`/applications/gst/${g.id}`}><button className="btn btn-outline btn-sm">Details →</button></Link>
        </div>
      </div>
      {g.status === 'REJECTED' ? (
        <div className="alert alert-error">❌ {g.adminNote || 'Application rejected. Contact our CA team.'}</div>
      ) : (
        <ProgressTracker steps={GST_STEPS} currentStatus={g.status} />
      )}
      {g.adminNote && g.status !== 'REJECTED' && (
        <div style={{ marginTop: '.75rem', background: 'var(--greenl)', border: '1px solid #a8d5b9', borderRadius: 8, padding: '.7rem 1rem', fontSize: '.82rem', color: 'var(--green)' }}>
          💬 CA: {g.adminNote}
        </div>
      )}
      <div style={{ fontSize: '.72rem', color: 'var(--muted)', marginTop: '.6rem' }}>
        Submitted {new Date(g.submittedAt).toLocaleDateString('en-IN')}
      </div>
    </div>
  );
}

function ProgressTracker({ steps, currentStatus }) {
  const idx = steps.findIndex(s => s.key === currentStatus);
  return (
    <div className="progress-track">
      {steps.map((s, i) => {
        const state = i < idx ? 'done' : i === idx ? 'active' : 'pending';
        return (
          <div key={s.key} className={`pt-step ${state}`}>
            <div className="pt-circle">{state === 'done' ? '✓' : i + 1}</div>
            <div className="pt-label">{s.label}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── ITR Application Detail ─────────────────────────────────────────
export function ItrApplicationDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [filing, setFiling]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [msg, setMsg] = useState(null);

  const load = () => api.get(`/api/filing/${id}`)
    .then(r => setFiling(r.data)).catch(console.error).finally(() => setLoading(false));

  useEffect(() => { load(); }, [id]);

  const handleApprove = async () => {
    setApproving(true);
    try {
      const { data } = await api.post(`/api/filing/${id}/approve`);
      setFiling(data);
      setMsg({ type: 'success', text: '✅ Draft approved! Our CA will now file your ITR.' });
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.error || 'Failed' }); }
    finally { setApproving(false); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { alert('Please provide a reason'); return; }
    setRejecting(true);
    try {
      const { data } = await api.post(`/api/filing/${id}/reject?reason=${encodeURIComponent(rejectReason)}`);
      setFiling(data); setShowReject(false);
      setMsg({ type: 'success', text: '📝 Change request sent to our CA team.' });
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.error || 'Failed' }); }
    finally { setRejecting(false); }
  };

  const downloadFile = async (filename, label) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/files/download/${filename}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = label + '_' + (filename.includes('_') ? filename.substring(filename.indexOf('_') + 1) : filename);
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { alert('Download failed'); }
  };

  if (loading) return <div className="centered"><span className="spinner spinner-dark" /></div>;
  if (!filing)  return <div className="centered"><p>Filing not found.</p></div>;

  const idx = ITR_STEPS.findIndex(s => s.key === filing.status);
  const currentStep = ITR_STEPS[idx];

  return (
    <>
      <PageTitle title={`ITR Filing #${filing.id}`} />
      <div className="page-hero">
        <div className="page-hero-label">ITR Filing Detail</div>
        <h1>{filing.itrType} — AY {filing.assessmentYr}</h1>
        <p>Filing #{filing.id} · {filing.pan}</p>
      </div>
      <div className="page">
        <button className="btn btn-outline btn-sm" style={{ marginBottom: '1.5rem' }} onClick={() => navigate('/applications')}>← Back</button>

        {msg && <div className={`alert alert-${msg.type}`} style={{ marginBottom: '1.5rem' }}>{msg.text}</div>}

        {/* Progress */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <h3 style={{ fontSize: '1rem', color: 'var(--navy)' }}>Filing Progress</h3>
            <StatusBadge status={filing.status} />
          </div>
          {filing.status === 'REJECTED' ? (
            <div className="alert alert-error">❌ {filing.adminNote}</div>
          ) : (
            <>
              <ProgressTracker steps={ITR_STEPS} currentStatus={filing.status} />
              {currentStep && (
                <div className="alert alert-info" style={{ marginTop: '1rem' }}>
                  📍 <strong>{currentStep.label}:</strong> {currentStep.desc}
                </div>
              )}
            </>
          )}
          {filing.adminNote && filing.status !== 'REJECTED' && (
            <div style={{ marginTop: '.75rem', background: 'var(--bluel)', border: '1px solid rgba(26,95,180,.15)', borderRadius: 8, padding: '.8rem 1rem', fontSize: '.85rem', color: 'var(--blue)' }}>
              💬 <strong>CA Note:</strong> {filing.adminNote}
            </div>
          )}
        </div>

        {/* Draft approval */}
        {filing.status === 'DRAFT_SENT' && (
          <div className="card" style={{ marginBottom: '1.5rem', border: '2px solid var(--gold)' }}>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
              <span style={{ fontSize: '2rem' }}>📋</span>
              <div style={{ flex: 1 }}>
                <h3 style={{ color: 'var(--navy)', fontSize: '1.05rem', marginBottom: '.4rem' }}>Your Draft ITR is Ready for Review</h3>
                <p style={{ color: 'var(--muted)', fontSize: '.88rem', marginBottom: '1.25rem' }}>
                  Download the draft, review all numbers carefully, then approve or request changes.
                </p>
                {filing.docDraftItr && (
                  <button onClick={() => downloadFile(filing.docDraftItr, 'Draft_ITR')}
                    style={{ background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 8, padding: '.55rem 1.2rem', fontSize: '.85rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '.4rem', marginBottom: '1.25rem' }}>
                    ⬇ Download Draft ITR PDF
                  </button>
                )}
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <button className="btn btn-gold" onClick={handleApprove} disabled={approving}>
                    {approving ? <><span className="spinner" /> Approving...</> : '✅ Approve & Proceed to Filing'}
                  </button>
                  <button className="btn btn-outline" style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
                    onClick={() => setShowReject(!showReject)}>
                    ✏️ Request Changes
                  </button>
                </div>
                {showReject && (
                  <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--redl)', borderRadius: 10, border: '1px solid #f5b8b8' }}>
                    <label style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--red)', display: 'block', marginBottom: '.5rem' }}>What needs to be changed?</label>
                    <textarea rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                      placeholder="e.g. The HRA deduction amount is incorrect..."
                      style={{ width: '100%', padding: '.6rem .9rem', border: '1.5px solid #f5b8b8', borderRadius: 8, fontFamily: 'DM Sans, sans-serif', fontSize: '.85rem', marginBottom: '.75rem', resize: 'vertical' }} />
                    <div style={{ display: 'flex', gap: '.75rem' }}>
                      <button className="btn btn-danger btn-sm" onClick={handleReject} disabled={rejecting}>
                        {rejecting ? 'Sending...' : 'Send Change Request'}
                      </button>
                      <button className="btn btn-outline btn-sm" onClick={() => setShowReject(false)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ITR-V download */}
        {filing.status === 'ACKNOWLEDGED' && filing.docItrV && (
          <div className="card" style={{ marginBottom: '1.5rem', border: '2px solid var(--green)', background: 'var(--greenl)' }}>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <span style={{ fontSize: '2.5rem' }}>🎉</span>
              <div style={{ flex: 1 }}>
                <h3 style={{ color: 'var(--green)', fontSize: '1.05rem', marginBottom: '.3rem' }}>ITR Successfully Filed & Acknowledged!</h3>
                <p style={{ color: 'var(--green)', fontSize: '.85rem', opacity: .8, marginBottom: '1rem' }}>Download your ITR-V acknowledgement for your records.</p>
                <button onClick={() => downloadFile(filing.docItrV, 'ITR-V_Acknowledgement')}
                  style={{ background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 8, padding: '.6rem 1.4rem', fontSize: '.88rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '.4rem' }}>
                  ⬇ Download ITR-V Acknowledgement
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ACTION_REQUIRED — Edit & Resubmit */}
        {filing.status === 'ACTION_REQUIRED' && (
          <ActionRequiredPanel filing={filing} onUpdated={f => { setFiling(f); setMsg({ type: 'success', text: '✅ Filing updated and resubmitted!' }); }} />
        )}

        {/* Filing details */}
        <div className="two-col">
          <div className="card">
            <SectionTitle>Personal Info</SectionTitle>
            <InfoRow label="Full Name" value={filing.fullName} />
            <InfoRow label="PAN"       value={filing.pan} />
            <InfoRow label="Email"     value={filing.email} />
            <InfoRow label="Phone"     value={filing.phone} />
            <InfoRow label="DOB"       value={filing.dob} />
            <InfoRow label="Status"    value={filing.residentialStatus} />
          </div>
          <div className="card">
            <SectionTitle>Income Details</SectionTitle>
            <InfoRow label="Annual Income" value={`₹${Number(filing.annualIncome || 0).toLocaleString('en-IN')}`} />
            <InfoRow label="TDS Paid"      value={`₹${Number(filing.taxPaid || 0).toLocaleString('en-IN')}`} />
            <InfoRow label="Deductions"    value={`₹${Number(filing.deductions || 0).toLocaleString('en-IN')}`} />
            <InfoRow label="Bank"          value={filing.bankName} />
            <InfoRow label="IFSC"          value={filing.ifsc} />
            <InfoRow label="Filing Type"   value={filing.filingType} />
          </div>
        </div>

        {/* Conversation Timeline */}
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <SectionTitle>Conversation & Timeline</SectionTitle>
          <ConversationTimeline filingId={id} />
        </div>
      </div>
    </>
  );
}

// ── Action Required Panel (Edit & Resubmit) ──────────────────────
function ActionRequiredPanel({ filing, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [data, setData] = useState({
    fullName: filing.fullName || '', pan: filing.pan || '', email: filing.email || '',
    phone: filing.phone || '', dob: filing.dob || '', gender: filing.gender || 'M',
    residentialStatus: filing.residentialStatus || 'resident', aadhar: filing.aadhar || '',
    address: filing.address || '', assessmentYr: filing.assessmentYr || '', itrType: filing.itrType || '',
    filingType: filing.filingType || 'original',
    annualIncome: filing.annualIncome || '', taxPaid: filing.taxPaid || '',
    deductions: filing.deductions || '', salaryIncome: filing.salaryIncome || '',
    businessIncome: filing.businessIncome || '', capitalGains: filing.capitalGains || '',
    otherIncome: filing.otherIncome || '', houseIncome: filing.houseIncome || '',
    bankName: filing.bankName || '', accountNo: filing.accountNo || '', ifsc: filing.ifsc || '',
    remarks: filing.remarks || '',
  });
  const [files, setFiles] = useState({});
  const [saving, setSaving] = useState(false);

  const handle = e => {
    const { name, value } = e.target;
    setData(d => ({ ...d, [name]: name === 'pan' || name === 'ifsc' ? value.toUpperCase() : value }));
  };

  const handleFile = (name, file) => {
    if (file && file.size > 5 * 1024 * 1024) { alert('Max 5MB'); return; }
    setFiles(f => ({ ...f, [name]: file }));
  };

  const submit = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(data).forEach(([k, v]) => { if (v) fd.append(k, v); });
      Object.entries(files).forEach(([k, v]) => { if (v) fd.append(k, v); });
      const { data: res } = await api.put(`/api/filing/${filing.id}/update`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUpdated(res);
      setEditing(false);
    } catch (err) { alert(err.response?.data?.error || 'Update failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ marginBottom: '1.5rem', border: '2px solid #E65100' }}>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '2rem' }}>⚠️</span>
        <div style={{ flex: 1 }}>
          <h3 style={{ color: '#E65100', fontSize: '1.05rem', marginBottom: '.4rem' }}>Additional Information Required</h3>
          <p style={{ color: 'var(--muted)', fontSize: '.88rem', marginBottom: '1rem' }}>
            Your CA has requested corrections or additional documents. Please review the note below and update your filing.
          </p>
          {filing.adminNote && (
            <div style={{ background: '#FFF3E0', border: '1px solid #FFCC80', borderRadius: 8, padding: '.8rem 1rem', fontSize: '.85rem', color: '#E65100', marginBottom: '1.25rem' }}>
              💬 <strong>CA Note:</strong> {filing.adminNote}
            </div>
          )}

          {!editing ? (
            <button className="btn btn-gold" onClick={() => setEditing(true)}>✏️ Edit & Resubmit Filing</button>
          ) : (
            <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: '1.25rem', background: 'var(--cream)' }}>
              <div style={{ fontWeight: 600, fontSize: '.85rem', color: 'var(--navy)', marginBottom: '1rem' }}>Edit Your Filing</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.75rem' }}>
                {[
                  ['fullName', 'Full Name'], ['pan', 'PAN'], ['email', 'Email'], ['phone', 'Phone'],
                  ['annualIncome', 'Annual Income (₹)'], ['taxPaid', 'TDS Paid (₹)'],
                  ['deductions', 'Deductions (₹)'], ['salaryIncome', 'Salary Income (₹)'],
                  ['bankName', 'Bank Name'], ['accountNo', 'Account No'], ['ifsc', 'IFSC'],
                ].map(([name, label]) => (
                  <div key={name} className="field">
                    <label>{label}</label>
                    <input name={name} value={data[name]} onChange={handle} />
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '1rem' }}>
                <div style={{ fontWeight: 600, fontSize: '.82rem', color: 'var(--navy)', marginBottom: '.5rem' }}>Upload Additional Documents</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem' }}>
                  {[['docForm16', 'Form 16'], ['docAis', 'AIS/26AS'], ['docBank', 'Bank Statement'], ['docOther', 'Other']].map(([name, label]) => (
                    <div key={name} className="field">
                      <label>{label}</label>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => handleFile(name, e.target.files[0])}
                        style={{ fontSize: '.78rem' }} />
                      {files[name] && <span style={{ fontSize: '.72rem', color: 'var(--green)' }}>📄 {files[name].name}</span>}
                    </div>
                  ))}
                </div>
              </div>
              <div className="field" style={{ marginTop: '.75rem' }}>
                <label>Additional Remarks</label>
                <textarea name="remarks" rows={2} value={data.remarks} onChange={handle} placeholder="Additional notes..." />
              </div>
              <div style={{ display: 'flex', gap: '.75rem', marginTop: '1rem' }}>
                <button className="btn btn-gold" onClick={submit} disabled={saving}>
                  {saving ? <><span className="spinner" /> Resubmitting...</> : '🚀 Update & Resubmit'}
                </button>
                <button className="btn btn-outline" onClick={() => setEditing(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── User Conversation Timeline ───────────────────────────────────
function ConversationTimeline({ filingId }) {
  const [comments, setComments] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.get(`/api/filing/${filingId}/comments`)
      .then(r => setComments(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, [filingId]);

  const send = async () => {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      await api.post(`/api/filing/${filingId}/comments`, { message: newMsg });
      setNewMsg('');
      load();
    } catch (e) { alert('Failed to send'); }
    finally { setSending(false); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '1rem' }}><span className="spinner spinner-dark" /></div>;

  return (
    <div>
      <div style={{ maxHeight: 400, overflowY: 'auto', marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
        {comments.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--muted)', fontSize: '.82rem', padding: '1rem' }}>No messages yet. Your CA will post updates here.</div>
        )}
        {comments.map(c => (
          <div key={c.id} style={{
            padding: '.6rem .9rem',
            borderRadius: 10,
            fontSize: '.82rem',
            background: c.senderType === 'SYSTEM' ? 'var(--creamd)' :
                         c.senderType === 'ADMIN'  ? 'var(--bluel)' : 'var(--greenl)',
            borderLeft: `3px solid ${c.senderType === 'SYSTEM' ? 'var(--muted)' :
                         c.senderType === 'ADMIN'  ? 'var(--blue)' : 'var(--green)'}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.2rem' }}>
              <span style={{ fontWeight: 600, color: c.senderType === 'SYSTEM' ? 'var(--muted)' :
                             c.senderType === 'ADMIN' ? 'var(--blue)' : 'var(--green)', fontSize: '.72rem' }}>
                {c.senderType === 'SYSTEM' ? '🔄 System' : c.senderType === 'ADMIN' ? '👨‍💼 CA Team' : '👤 You'}
              </span>
              <span style={{ fontSize: '.68rem', color: 'var(--muted)' }}>
                {new Date(c.createdAt).toLocaleString('en-IN')}
              </span>
            </div>
            <div style={{ color: 'var(--text)' }}>{c.message}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '.5rem' }}>
        <input
          value={newMsg}
          onChange={e => setNewMsg(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && send()}
          placeholder="Type a message to your CA..."
          style={{ flex: 1, padding: '.5rem .75rem', border: '1.5px solid var(--border)', borderRadius: 8, fontFamily: 'DM Sans, sans-serif', fontSize: '.82rem' }}
        />
        <button onClick={send} disabled={sending || !newMsg.trim()}
          style={{ background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 8, padding: '.5rem 1rem', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          {sending ? '...' : '📨 Send'}
        </button>
      </div>
    </div>
  );
}

// ── GST Application Detail ─────────────────────────────────────────
export function GstApplicationDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [app, setApp]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/api/gst/${id}`).then(r => setApp(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  const downloadFile = async (filename, label) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/files/download/${filename}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = label + '_' + (filename.includes('_') ? filename.substring(filename.indexOf('_') + 1) : filename);
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { alert('Download failed'); }
  };

  if (loading) return <div className="centered"><span className="spinner spinner-dark" /></div>;
  if (!app)    return <div className="centered"><p>Application not found.</p></div>;

  const idx = GST_STEPS.findIndex(s => s.key === app.status);
  const currentStep = GST_STEPS[idx];

  return (
    <>
      <PageTitle title={`GST Application #${app.id}`} />
      <div className="page-hero">
        <div className="page-hero-label">GST Registration Detail</div>
        <h1>{app.businessName}</h1>
        <p>Application #{app.id} · {app.businessType} · {app.pan}</p>
      </div>
      <div className="page">
        <button className="btn btn-outline btn-sm" style={{ marginBottom: '1.5rem' }} onClick={() => navigate('/applications')}>← Back</button>

        {/* Progress */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <h3 style={{ fontSize: '1rem', color: 'var(--navy)' }}>Registration Progress</h3>
            <GstBadge status={app.status} />
          </div>
          {app.status === 'REJECTED' ? (
            <div className="alert alert-error">❌ {app.adminNote || 'Application rejected. Please contact our CA team.'}</div>
          ) : (
            <>
              <ProgressTracker steps={GST_STEPS} currentStatus={app.status} />
              {currentStep && (
                <div className="alert alert-info" style={{ marginTop: '1rem' }}>
                  📍 <strong>{currentStep.label}:</strong> {currentStep.desc}
                </div>
              )}
            </>
          )}
          {app.adminNote && app.status !== 'REJECTED' && (
            <div style={{ marginTop: '.75rem', background: 'var(--greenl)', border: '1px solid #a8d5b9', borderRadius: 8, padding: '.8rem 1rem', fontSize: '.85rem', color: 'var(--green)' }}>
              💬 <strong>CA Note:</strong> {app.adminNote}
            </div>
          )}
          {app.gstin && (
            <div style={{ marginTop: '1rem', background: 'var(--greenl)', border: '2px solid var(--green)', borderRadius: 10, padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '.72rem', color: 'var(--green)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em' }}>Your GSTIN</div>
                <div style={{ fontFamily: 'monospace', fontSize: '1.2rem', fontWeight: 700, color: 'var(--green)', letterSpacing: '.08em' }}>{app.gstin}</div>
              </div>
              {app.docGstCertificate && (
                <button onClick={() => downloadFile(app.docGstCertificate, 'GST_Certificate')}
                  style={{ background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 8, padding: '.6rem 1.2rem', fontSize: '.85rem', fontWeight: 600, cursor: 'pointer' }}>
                  ⬇ Download Certificate
                </button>
              )}
            </div>
          )}
        </div>

        {/* Business details */}
        <div className="two-col">
          <div className="card">
            <SectionTitle>Business Information</SectionTitle>
            <InfoRow label="Business Name"  value={app.businessName} />
            <InfoRow label="Type"           value={app.businessType} />
            <InfoRow label="Category"       value={app.businessCategory} />
            <InfoRow label="Turnover"       value={app.turnover} />
            <InfoRow label="PAN"            value={app.pan} />
            <InfoRow label="State"          value={app.state} />
            <InfoRow label="Address"        value={app.businessAddress} />
          </div>
          <div className="card">
            <SectionTitle>Owner & Contact</SectionTitle>
            <InfoRow label="Owner Name" value={app.ownerName} />
            <InfoRow label="Email"      value={app.email} />
            <InfoRow label="Phone"      value={app.phone} />
            <InfoRow label="Bank"       value={app.bankName} />
            <InfoRow label="Account No" value={app.accountNo} />
            <InfoRow label="IFSC"       value={app.ifsc} />
          </div>
        </div>
      </div>
    </>
  );
}

// ── Helpers ───────────────────────────────────────────────────────
function SectionTitle({ children }) {
  return <div style={{ fontWeight: 600, fontSize: '.82rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--navy)', marginBottom: '1rem' }}>{children}</div>;
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '.4rem 0', borderBottom: '1px solid var(--creamd)', fontSize: '.84rem' }}>
      <span style={{ color: 'var(--muted)' }}>{label}</span>
      <span style={{ fontWeight: 500, color: 'var(--navy)' }}>{value}</span>
    </div>
  );
}

function TimelineRow({ icon, label, date }) {
  if (!date) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '.85rem', padding: '.4rem 0', borderBottom: '1px solid var(--creamd)' }}>
      <span>{icon}</span>
      <span style={{ color: 'var(--muted)', minWidth: 140 }}>{label}</span>
      <span style={{ color: 'var(--navy)', fontWeight: 500 }}>{new Date(date).toLocaleString('en-IN')}</span>
    </div>
  );
}

// ── GST Return Detail ──────────────────────────────────────────
export function GstReturnDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [ret, setRet]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/api/gst-return/${id}`).then(r => setRet(r.data))
      .catch(console.error).finally(() => setLoading(false));
  }, [id]);

  const downloadFile = async (filename, label) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/files/download/${filename}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = label + '.pdf';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { alert('Download failed'); }
  };

  if (loading) return <div className="centered"><span className="spinner spinner-dark" /></div>;
  if (!ret)    return <div className="centered"><p>Return not found.</p></div>;

  const STATUS = { SUBMITTED: { label: 'Submitted', icon: '📨', cls: 'badge-submitted' }, UNDER_REVIEW: { label: 'Under Review', icon: '🔍', cls: 'badge-under_review' }, FILED: { label: 'Filed', icon: '✅', cls: 'badge-filed' } };
  const s = STATUS[ret.status] || { label: ret.status, icon: '•', cls: '' };

  return (
    <>
      <PageTitle title={`GST Return #${ret.id}`} />
      <div className="page-hero">
        <div className="page-hero-label">GST Return Detail</div>
        <h1>{ret.returnType} — {ret.filingPeriod}</h1>
        <p>#{ret.id} · {ret.gstin} · {ret.businessName}</p>
      </div>
      <div className="page">
        <button className="btn btn-outline btn-sm" style={{ marginBottom: '1.5rem' }} onClick={() => navigate('/dashboard')}>← Back</button>

        {/* Status */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <h3 style={{ fontSize: '1rem', color: 'var(--navy)' }}>Return Status</h3>
            <span className={`badge ${s.cls}`}>{s.icon} {s.label}</span>
          </div>
          <div className="progress-track">
            {[{key:'SUBMITTED',label:'Submitted',desc:'Return received by CA team.'},{key:'UNDER_REVIEW',label:'Under Review',desc:'CA is reviewing and preparing your return.'},{key:'FILED',label:'Filed',desc:'Return filed on GST portal. ARN generated.'}].map((step, i) => {
              const order = ['SUBMITTED','UNDER_REVIEW','FILED'];
              const idx = order.indexOf(ret.status);
              const state = i < idx ? 'done' : i === idx ? 'active' : 'pending';
              return (
                <div key={step.key} className={`pt-step ${state}`}>
                  <div className="pt-circle">{state === 'done' ? '✓' : i+1}</div>
                  <div className="pt-label">{step.label}</div>
                </div>
              );
            })}
          </div>
          {ret.adminNote && (
            <div style={{ marginTop: '1rem', background: '#EDE7F6', border: '1px solid #CE93D8', borderRadius: 8, padding: '.8rem 1rem', fontSize: '.85rem', color: '#512DA8' }}>
              💬 <strong>CA Note:</strong> {ret.adminNote}
            </div>
          )}
          {ret.arn && (
            <div style={{ marginTop: '1rem', background: 'var(--greenl)', border: '1px solid #a8d5b9', borderRadius: 8, padding: '.75rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '.72rem', color: 'var(--green)', fontWeight: 600, textTransform: 'uppercase' }}>Acknowledgement Reference Number (ARN)</div>
                <div style={{ fontFamily: 'monospace', fontSize: '1rem', fontWeight: 700, color: 'var(--green)', marginTop: '.15rem' }}>{ret.arn}</div>
              </div>
              {ret.docFiledReturn && (
                <button onClick={() => downloadFile(ret.docFiledReturn, 'Filed_GST_Return')}
                  style={{ background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 8, padding: '.55rem 1.2rem', fontSize: '.85rem', fontWeight: 600, cursor: 'pointer' }}>
                  ⬇ Download Filed Return
                </button>
              )}
            </div>
          )}
        </div>

        <div className="two-col">
          <div className="card">
            <SectionTitle>Return Details</SectionTitle>
            <InfoRow label="GSTIN"         value={ret.gstin} />
            <InfoRow label="Business"      value={ret.businessName} />
            <InfoRow label="Return Type"   value={ret.returnType} />
            <InfoRow label="Filing Period" value={ret.filingPeriod} />
            <InfoRow label="Email"         value={ret.email} />
            <InfoRow label="Phone"         value={ret.phone} />
          </div>
          <div className="card">
            <SectionTitle>Tax Summary</SectionTitle>
            <InfoRow label="Total Turnover"   value={ret.totalTurnover ? `₹${Number(ret.totalTurnover).toLocaleString('en-IN')}` : null} />
            <InfoRow label="Output GST"       value={ret.totalTaxCollected ? `₹${Number(ret.totalTaxCollected).toLocaleString('en-IN')}` : null} />
            <InfoRow label="ITC Available"    value={ret.itcAvailable ? `₹${Number(ret.itcAvailable).toLocaleString('en-IN')}` : null} />
            <InfoRow label="Net Tax Payable"  value={ret.taxPayable ? `₹${Number(ret.taxPayable).toLocaleString('en-IN')}` : null} />
            <InfoRow label="Tax Paid"         value={ret.taxPaid ? `₹${Number(ret.taxPaid).toLocaleString('en-IN')}` : null} />
          </div>
        </div>
      </div>
    </>
  );
}

// ── DSC Application Detail ─────────────────────────────────────
export function DscApplicationDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [app, setApp]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/api/dsc/${id}`).then(r => setApp(r.data))
      .catch(console.error).finally(() => setLoading(false));
  }, [id]);

  const downloadFile = async (filename, label) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/files/download/${filename}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = label + '.pdf';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { alert('Download failed'); }
  };

  if (loading) return <div className="centered"><span className="spinner spinner-dark" /></div>;
  if (!app)    return <div className="centered"><p>Application not found.</p></div>;

  const STATUS = {
    APPLIED:                    { label: 'Applied',            icon: '📨', cls: 'badge-submitted' },
    DOCUMENTS_VERIFIED:         { label: 'Docs Verified',      icon: '✅', cls: 'badge-approved' },
    SUBMITTED_TO_PROVIDER:      { label: 'With Provider',      icon: '🔄', cls: 'badge-under_review' },
    VIDEO_VERIFICATION_PENDING: { label: 'Video Verification', icon: '🎥', cls: 'badge-draft_sent' },
    ISSUED:                     { label: 'Issued',             icon: '🔐', cls: 'badge-acknowledged' },
    REJECTED:                   { label: 'Rejected',           icon: '❌', cls: 'badge-rejected' },
  };
  const s = STATUS[app.status] || { label: app.status, icon: '•', cls: '' };
  const STEP_KEYS = ['APPLIED','DOCUMENTS_VERIFIED','SUBMITTED_TO_PROVIDER','VIDEO_VERIFICATION_PENDING','ISSUED'];
  const STEP_LABELS = ['Applied','Verified','Provider','Video','Issued'];
  const idx = STEP_KEYS.indexOf(app.status);

  return (
    <>
      <PageTitle title={`DSC Application #${app.id}`} />
      <div className="page-hero">
        <div className="page-hero-label">DSC Application Detail</div>
        <h1>{app.dscType}</h1>
        <p>#{app.id} · {app.validity} · {app.tokenType}</p>
      </div>
      <div className="page">
        <button className="btn btn-outline btn-sm" style={{ marginBottom: '1.5rem' }} onClick={() => navigate('/dashboard')}>← Back</button>

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <h3 style={{ fontSize: '1rem', color: 'var(--navy)' }}>Application Progress</h3>
            <span className={`badge ${s.cls}`}>{s.icon} {s.label}</span>
          </div>

          {app.status !== 'REJECTED' && (
            <div className="progress-track">
              {STEP_KEYS.map((key, i) => {
                const state = i < idx ? 'done' : i === idx ? 'active' : 'pending';
                return (
                  <div key={key} className={`pt-step ${state}`}>
                    <div className="pt-circle">{state === 'done' ? '✓' : i+1}</div>
                    <div className="pt-label">{STEP_LABELS[i]}</div>
                  </div>
                );
              })}
            </div>
          )}

          {app.status === 'REJECTED' && (
            <div className="alert alert-error">❌ {app.adminNote || 'Application rejected. Please contact our team.'}</div>
          )}
          {app.status === 'VIDEO_VERIFICATION_PENDING' && (
            <div className="alert alert-info" style={{ marginTop: '1rem' }}>
              🎥 Our team will call <strong>{app.phone}</strong> to schedule your video verification call. Please keep your phone handy.
            </div>
          )}
          {app.adminNote && !['REJECTED','VIDEO_VERIFICATION_PENDING'].includes(app.status) && (
            <div style={{ marginTop: '1rem', background: '#EDE7F6', border: '1px solid #CE93D8', borderRadius: 8, padding: '.8rem 1rem', fontSize: '.85rem', color: '#4527A0' }}>
              💬 <strong>Team Note:</strong> {app.adminNote}
            </div>
          )}

          {/* Tracking info */}
          {app.trackingId && (
            <div style={{ marginTop: '1rem', background: 'var(--bluel)', border: '1px solid rgba(26,95,180,.15)', borderRadius: 8, padding: '.75rem 1rem' }}>
              <div style={{ fontSize: '.72rem', color: 'var(--blue)', fontWeight: 600, textTransform: 'uppercase' }}>Provider Tracking ID</div>
              <div style={{ fontFamily: 'monospace', fontSize: '1rem', fontWeight: 700, color: 'var(--blue)', marginTop: '.15rem' }}>{app.trackingId}</div>
            </div>
          )}

          {/* DSC issued */}
          {app.status === 'ISSUED' && app.docDscDetails && (
            <div style={{ marginTop: '1rem', background: 'var(--greenl)', border: '2px solid var(--green)', borderRadius: 10, padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600, color: 'var(--green)' }}>🔐 Your DSC has been issued!</div>
                {app.serialNumber && <div style={{ fontFamily: 'monospace', fontSize: '.85rem', color: 'var(--green)', marginTop: '.2rem' }}>S/N: {app.serialNumber}</div>}
              </div>
              <button onClick={() => downloadFile(app.docDscDetails, 'DSC_Details')}
                style={{ background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 8, padding: '.6rem 1.2rem', fontSize: '.85rem', fontWeight: 600, cursor: 'pointer' }}>
                ⬇ Download Details
              </button>
            </div>
          )}
        </div>

        <div className="two-col">
          <div className="card">
            <SectionTitle>DSC & Applicant Details</SectionTitle>
            <InfoRow label="DSC Type"    value={app.dscType} />
            <InfoRow label="Validity"    value={app.validity} />
            <InfoRow label="Token Type"  value={app.tokenType} />
            <InfoRow label="Applicant"   value={app.applicantName} />
            <InfoRow label="PAN"         value={app.pan} />
            <InfoRow label="Date of Birth" value={app.dob} />
            <InfoRow label="Email"       value={app.email} />
            <InfoRow label="Phone"       value={app.phone} />
            {app.organisationName && <InfoRow label="Organisation" value={app.organisationName} />}
          </div>
          <div className="card">
            <SectionTitle>Address</SectionTitle>
            <InfoRow label="Address"  value={app.address} />
            <InfoRow label="City"     value={app.city} />
            <InfoRow label="State"    value={app.state} />
            <InfoRow label="Pincode"  value={app.pincode} />
          </div>
        </div>
      </div>
    </>
  );
}